import { Box, Button, Dialog, IconButton, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ArrowForwardRounded, PictureAsPdfRounded, PlayArrowRounded } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import VideoPlayer from '../../common/video-player/VideoPlayer'
import CustomTabs from '../../common/CustomTabs'
import AddTutorialDialog from './AddTutorialDialog'
import AddManualDialog from './AddManualDialog'
import ConfirmDialog from '../../common/ConfirmDialog'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'
import { DrawerDocumentViewer } from '../../common/DocumentViewer'

const HELP_CENTER_TABS = [
    {
        value: 'tutorials',
        label: 'Tutorials',
    },
    {
        value: 'manuals',
        label: 'Manuals',
    },
]

const AUDIENCE_TABS = [
    {
        value: 'ALL',
        label: 'All',
    },
    {
        value: 'STUDENT',
        label: 'Student',
    },
    {
        value: 'FACULTY',
        label: 'Faculty',
    },
    {
        value: 'HOD',
        label: 'HOD',
    },
    {
        value: 'PRINCIPAL',
        label: 'Principal',
    },
]

const ROLE_LABELS = {
    STUDENT: 'Student',
    FACULTY: 'Faculty',
    HOD: 'HOD',
    PRINCIPAL: 'Principal',
}

const getFileDetails = ( item ) => {
    const details = item?.file_details
    if ( !details ) return {}
    if ( typeof details === 'object' ) return details
    try {
        return JSON.parse( details )
    } catch ( err ) {
        return {}
    }
}

const getTutorialDuration = ( tutorial, runtimeDurations = {} ) => {
    const fileDetails = getFileDetails( tutorial )
    const storedDuration = Number( fileDetails?.duration )
    if ( storedDuration && !Number.isNaN( storedDuration ) ) {
        return storedDuration
    }
    const runtimeDuration = Number( runtimeDurations[tutorial?.id] )

    if ( runtimeDuration && !Number.isNaN( runtimeDuration ) ) {
        return runtimeDuration
    }
    return 0
}

const getDurationLabel = ( duration ) => {
    const value = Number( duration )
    if ( !value || Number.isNaN( value ) ) {
        return null
    }
    if ( value < 60 ) return `${Math.round( value )} sec`
    const minutes = Math.floor( value / 60 )
    const seconds = Math.round( value % 60 )
    if ( seconds === 0 ) return `${minutes} min`
    return `${minutes} min ${seconds} sec`
}

const getCardDurationLabel = ( duration ) => {
    const value = Number( duration )
    if ( !value || Number.isNaN( value ) ) {
        return '00:00'
    }
    const totalSeconds = Math.floor( value )
    const hours = Math.floor( totalSeconds / 3600 )
    const minutes = Math.floor( ( totalSeconds % 3600 ) / 60 )
    const seconds = totalSeconds % 60
    if ( hours > 0 ) {
        return `${String( hours ).padStart( 2, '0' )}:${String( minutes ).padStart( 2, '0' )}:${String( seconds ).padStart( 2, '0' )}`
    }
    return `${String( minutes ).padStart( 2, '0' )}:${String( seconds ).padStart( 2, '0' )}`
}

const getFileSizeLabel = ( size ) => {
    const value = Number( size )
    if ( !value || Number.isNaN( value ) ) {
        return null
    }
    const mb = value / ( 1024 * 1024 )
    return `${mb.toFixed( 1 )} MB`
}

const TutorialsSection = ( { auth, contentType = 'tutorials', showTabs = true } ) => {

    const normalizedAuth = auth?.toLowerCase() || ''
    const isAdmin = ['admin', 'subadmin'].includes( normalizedAuth )

    const [activeTab, setActiveTab] = useState( contentType )
    const [tutorials, setTutorials] = useState( [] )

    const [manuals, setManuals] = useState( [] )

    const [loading, setLoading] = useState( false )
    const [error, setError] = useState( null )
    const [search, setSearch] = useState( '' )
    const [audienceFilter, setAudienceFilter] = useState( 'ALL' )
    const [addDialogOpen, setAddDialogOpen] = useState( false )
    const [editingTutorial, setEditingTutorial] = useState( null )
    const [manualDialogOpen, setManualDialogOpen] = useState( false )
    const [editingManual, setEditingManual] = useState( null )
    const [selectedTutorial, setSelectedTutorial] = useState( null )
    const [viewerOpen, setViewerOpen] = useState( false )
    const [videoDurations, setVideoDurations] = useState( {} )
    const [previewFile, setPreviewFile] = useState( null )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [deleteTarget, setDeleteTarget] = useState( null )
    const [deleting, setDeleting] = useState( false )

    const { palette, border, layout, breakpoints } = useTheme()
    const xs = useMediaQuery( breakpoints.down( 'sm' ) )


    // FETCH TUTORIALS
    const fetchTutorials = useCallback( async () => {
        try {
            setLoading( true )
            setError( null )
            const { data } = await api.tutorialsAndManuals.getTutorials()
            setTutorials( data?.data || [] )

        } catch ( err ) {
            console.log( 'Failed to fetch tutorials:', err )
            setError( err?.response?.data?.message || 'Failed to load tutorials.' )
        } finally {
            setLoading( false )
        }
    }, [] )

    // |--------------------------------------------------------------------------
    // | FETCH MANUALS
    // |--------------------------------------------------------------------------
    const fetchManuals = useCallback( async () => {
        try {
            setLoading( true )
            setError( null )
            const { data } = await api.tutorialsAndManuals.getManuals()
            setManuals( data?.data || [] )

        } catch ( err ) {
            console.log( 'Failed to fetch manuals:', err )
            setError( err?.response?.data?.message || 'Failed to load manuals.' )
        } finally {
            setLoading( false )
        }
    }, [] )

    useEffect( () => {
        if ( !showTabs ) {
            setActiveTab( contentType )
        }
    }, [contentType, showTabs] )

    // FETCH DATA WHEN TAB CHANGES
    useEffect( () => {
        if ( activeTab === 'manuals' ) {
            fetchManuals()
        } else {
            fetchTutorials()
        }
    }, [activeTab, fetchTutorials, fetchManuals] )

    const handleMainTabChange = ( value ) => {
        setActiveTab( value )
        setSearch( '' )
        setAudienceFilter( 'ALL' )
    }

    // FILTER TUTORIALS
    const filteredTutorials = useMemo( () => {
        const searchTerm = search.trim().toLowerCase()
        return tutorials.filter( ( tutorial ) => {
            if ( isAdmin && audienceFilter !== 'ALL' ) {
                const audiences = Array.isArray( tutorial.audiences ) ? tutorial.audiences : []
                if ( !audiences.includes( audienceFilter ) ) {
                    return false
                }
            }
            if ( !searchTerm ) return true
            const title = tutorial.title?.toLowerCase() || ''
            const module = tutorial.module?.toLowerCase() || ''
            return title.includes( searchTerm ) || module.includes( searchTerm )
        } )
    }, [tutorials, search, audienceFilter, isAdmin] )

    // FILTER MANUALS
    const filteredManuals = useMemo( () => {
        const searchTerm = search.trim().toLowerCase()
        return manuals.filter( ( manual ) => {
            if ( isAdmin && audienceFilter !== 'ALL' ) {
                const audiences = Array.isArray( manual.audiences ) ? manual.audiences : []
                if ( !audiences.includes( audienceFilter ) ) {
                    return false
                }
            }
            if ( !searchTerm ) return true
            const title = manual.title?.toLowerCase() || ''
            const module = manual.module?.toLowerCase() || ''
            return title.includes( searchTerm ) || module.includes( searchTerm )
        } )
    }, [manuals, search, audienceFilter, isAdmin] )

    // GROUP TUTORIALS BY MODULE
    const tutorialSections = useMemo( () => {
        const grouped = {}
        filteredTutorials.filter( tutorial => tutorial.file_key && tutorial.video_url ).forEach( tutorial => {
            const module = tutorial.module || 'Other'
            if ( !grouped[module] ) grouped[module] = []
            grouped[module].push( tutorial )
        } )
        return Object.entries( grouped ).map( ( [section, videos] ) => ( {
            section,
            videos: videos.sort( ( a, b ) => {
                const orderDifference = Number( a.display_order || 0 ) - Number( b.display_order || 0 )
                if ( orderDifference !== 0 ) return orderDifference
                return String( a.title || '' ).localeCompare( String( b.title || '' ) )
            } )
        } ) ).sort( ( a, b ) => a.section.localeCompare( b.section ) )
    }, [filteredTutorials] )

    // GROUP MANUALS BY MODULE
    const manualSections = useMemo( () => {
        const grouped = {}
        filteredManuals.filter( manual => manual.file_key && manual.file_url ).forEach( manual => {
            const module = manual.module || 'Other'
            if ( !grouped[module] ) grouped[module] = []
            grouped[module].push( manual )
        } )
        return Object.entries( grouped ).map( ( [section, files] ) => ( {
            section,
            files: files.sort( ( a, b ) => {
                const orderDifference = Number( a.display_order || 0 ) - Number( b.display_order || 0 )
                if ( orderDifference !== 0 ) return orderDifference
                return String( a.title || '' ).localeCompare( String( b.title || '' ) )
            } )
        } ) ).sort( ( a, b ) => a.section.localeCompare( b.section ) )
    }, [filteredManuals] )

    // TUTORIAL ACTIONS
    const openAddTutorial = () => {
        setEditingTutorial( null )
        setAddDialogOpen( true )
    }
    const openEditTutorial = ( tutorial ) => {
        setEditingTutorial( tutorial )
        setAddDialogOpen( true )
    }
    const requestDelete = ( type, item ) => {
        setDeleteTarget( { type, item } )
    }

    const cancelDelete = () => {
        if ( deleting ) return
        setDeleteTarget( null )
    }

    const confirmDelete = async () => {
        if ( !deleteTarget ) return
        try {
            setDeleting( true )
            if ( deleteTarget.type === 'tutorial' ) {
                await api.tutorialsAndManuals.deleteTutorial( deleteTarget.item.id )
                toast.success( 'Tutorial deleted successfully.' )
                await fetchTutorials()
            } else {
                await api.tutorialsAndManuals.deleteManual( deleteTarget.item.id )
                toast.success( 'Manual deleted successfully.' )
                await fetchManuals()
            }
        } catch ( err ) {
            console.log( 'Delete content error:', err )
            const contentTypeLabel = deleteTarget.type === 'tutorial' ? 'tutorial' : 'manual'
            toast.error( err?.response?.data?.message || `Failed to delete ${contentTypeLabel}.` )
        } finally {
            setDeleting( false )
            setDeleteTarget( null )
        }
    }

    const deleteTutorial = ( tutorial ) => requestDelete( 'tutorial', tutorial )

    // MANUAL ACTIONS
    const openAddManual = () => {
        setEditingManual( null )
        setManualDialogOpen( true )
    }

    const openEditManual = ( manual ) => {
        setEditingManual( manual )
        setManualDialogOpen( true )
    }

    const deleteManual = ( manual ) => requestDelete( 'manual', manual )
    const openManual = ( manual ) => {
        if ( !manual?.file_url ) {
            toast.error( 'Manual file is unavailable.' )
            return
        }
        const fileDetails = getFileDetails( manual )
        setPreviewingFileDetails( {
            ...fileDetails,
            name: fileDetails?.name || manual.title || 'Manual.pdf',
            type: 'pdf',
        } )
        setPreviewFile( manual.file_url )
    }

    // TUTORIAL VIEWER
    const openTutorialViewer = ( tutorial ) => {
        setSelectedTutorial( tutorial )
        setViewerOpen( true )
    }
    const closeTutorialViewer = () => {
        setViewerOpen( false )
        setSelectedTutorial( null )
    }
    const getViewerVideoHeight = ( tutorial ) => {
        const fileDetails = getFileDetails( tutorial )
        const orientation = fileDetails?.videoOrientation
        if ( orientation === 'portrait' ) {
            return xs ? '420px' : '500px'
        }
        if ( orientation === 'square' ) {
            return xs ? '350px' : '500px'
        }
        return 'auto'
    }
    const currentSections = activeTab === 'manuals' ? manualSections : tutorialSections
    const currentLabel = activeTab === 'manuals' ? 'manual' : 'tutorial'
    const retryCurrent = activeTab === 'manuals' ? fetchManuals : fetchTutorials


    return (
        <Box display='flex' flexDirection='column' gap={1} marginTop='20px'>
            {/* MAIN HELP CENTER TABS */}

            {showTabs && (
                <Box sx={{ overflowX: 'auto', }} >
                    <CustomTabs value={activeTab} handleTabChange={handleMainTabChange} tabs={HELP_CENTER_TABS} variant='aligned-left' />
                </Box>
            )}

            {/* TOP ACTION BAR */}

            <Box
                display='flex'
                flexDirection={{
                    xs: 'column',
                    sm: 'row',
                }}
                alignItems={{
                    xs: 'stretch',
                    sm: 'center',
                }}
                justifyContent='space-between'
                gap='10px'
            >
                <Box>
                    {!isAdmin && (
                        <>
                            <Typography variant='h1' fontWeight='600' component='h2' fontSize='16px'>
                                {activeTab === 'manuals' ? 'Manuals' : 'Tutorials'}
                            </Typography>

                            <Typography variant='subtitle2' fontSize='12px' color='textSecondary'>
                                {activeTab === 'manuals' ? 'Access module-wise PDF manuals and documentation.' : 'Watch module-wise walkthroughs and learn how to use Alive.'}
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* SEARCH */}

            <Box
                display='flex'
                flexWrap="wrap"
                gap='10px'
            >
                <TextField
                    size='small'
                    value={search}
                    onChange={( event ) => setSearch( event.target.value )}
                    placeholder={activeTab === 'manuals' ? 'Search by manual name or module...' : 'Search by tutorial name or module...'}
                    sx={{
                        width: {
                            xs: '100%',
                            sm: '380px',
                        },
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position='start'>{Icons.default.Search}</InputAdornment>,
                    }}
                />

                {isAdmin && (
                    <Button variant='contained' disableElevation size='small' startIcon={Icons.default.AddIcon} onClick={activeTab === 'manuals' ? openAddManual : openAddTutorial}>
                        {activeTab === 'manuals' ? 'Add File' : 'Add Tutorial'}
                    </Button>
                )}
            </Box>

            {/* ADMIN AUDIENCE FILTER */}

            {isAdmin && (
                <Box sx={{ overflowX: 'auto', }} >
                    <CustomTabs value={audienceFilter} handleTabChange={setAudienceFilter} tabs={AUDIENCE_TABS} variant='aligned-left' />
                </Box>
            )}

            {/* LOADING */}

            {loading && (
                <Typography variant='subtitle2' color='textSecondary'>
                    {activeTab === 'manuals' ? 'Loading manuals...' : 'Loading tutorials...'}
                </Typography>
            )}

            {/* ERROR */}

            {!loading && error && (
                <Box display='flex' alignItems='center' gap='10px'>
                    <Typography variant='subtitle2' color='error'>
                        {error}
                    </Typography>

                    <Button size='small' variant='text' onClick={retryCurrent}>
                        Retry
                    </Button>
                </Box>
            )}

            {/* EMPTY */}

            {!loading && !error && currentSections.length === 0 && (
                <Box bgcolor={palette.contentBg} border={border[1]} borderRadius={layout.borderRadius} padding='30px 20px'>
                    <Typography variant='subtitle2' color='textSecondary' textAlign='center'>
                        {search ? `No ${currentLabel}s match your search.` : `No ${currentLabel}s available yet.`}
                    </Typography>
                </Box>
            )}

            {/* TUTORIAL MODULE SECTIONS */}

            {activeTab === 'tutorials' &&
                !loading &&
                !error &&
                tutorialSections.map( ( section ) => (
                    <Box key={section.section}>
                        <Box display='flex' justifyContent='space-between' alignItems='center' gap='10px' marginBottom='10px'>
                            <Typography
                                variant='subtitle2'
                                fontWeight='600'
                                color='textSecondary'
                                sx={{
                                    textTransform: 'uppercase',
                                }}
                            >
                                {section.section}
                            </Typography>

                            <Typography variant='caption' color='textSecondary'>
                                {section.videos.length} {section.videos.length === 1 ? 'video' : 'videos'}
                            </Typography>
                        </Box>

                        <Box
                            display='grid'
                            gridTemplateColumns={{
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(3, minmax(0, 1fr))',
                            }}
                            gap={layout.cardsGap || '10px'}
                        >
                            {section.videos.map( ( video ) => {
                                const duration = getTutorialDuration( video, videoDurations )
                                const durationLabel = getCardDurationLabel( duration )
                                return (
                                    <Box
                                        key={video.id}
                                        bgcolor={palette.contentBg}
                                        border={border[1]}
                                        borderRadius='12px'
                                        overflow='hidden'
                                        onClick={() => openTutorialViewer( video )}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'all 180ms ease',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            '&:hover': {
                                                boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                aspectRatio: '16 / 9',
                                                bgcolor: '#111',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                component='video'
                                                src={video.video_url}
                                                muted
                                                playsInline
                                                preload='metadata'
                                                onLoadedMetadata={( event ) => {
                                                    try {
                                                        const detectedDuration = Number( event.currentTarget.duration )
                                                        if ( detectedDuration && !Number.isNaN( detectedDuration ) ) {
                                                            setVideoDurations( ( previous ) => {
                                                                if ( previous[video.id] === detectedDuration ) {
                                                                    return previous
                                                                }
                                                                return {
                                                                    ...previous,
                                                                    [video.id]: detectedDuration,
                                                                }
                                                            } )
                                                        }
                                                        if ( detectedDuration > 0.1 ) {
                                                            event.currentTarget.currentTime = 0.1
                                                        }
                                                    } catch ( err ) {
                                                        // Thumbnail failure should not affect the card.
                                                    }
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'block',
                                                    objectFit: 'cover',
                                                    pointerEvents: 'none',
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'rgba(0,0,0,0.06)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: '50%',
                                                        bgcolor: 'rgba(255,255,255,0.96)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                                                    }}
                                                >
                                                    <PlayArrowRounded
                                                        sx={{
                                                            fontSize: 30,
                                                            color: 'primary.main',
                                                            ml: '2px',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box padding='14px' display='flex' flexDirection='column' flexGrow={1}>
                                            <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap='8px'>
                                                <Typography
                                                    variant='subtitle2'
                                                    component='h3'
                                                    fontWeight='600'
                                                    color='textPrimary'
                                                    sx={{
                                                        fontSize: '14px',
                                                        lineHeight: 1.4,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {video.title}
                                                </Typography>

                                                {isAdmin && (
                                                    <Box display='flex' gap='2px' flexShrink={0} marginTop='-5px' marginRight='-5px'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={( event ) => {
                                                                event.stopPropagation()
                                                                openEditTutorial( video )
                                                            }}
                                                        >
                                                            {Icons.default.EditIcon}
                                                        </IconButton>

                                                        <IconButton
                                                            size='small'
                                                            color='error'
                                                            onClick={( event ) => {
                                                                event.stopPropagation()
                                                                deleteTutorial( video )
                                                            }}
                                                        >
                                                            {Icons.default.DeleteIcon}
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Typography
                                                variant='body2'
                                                color='textSecondary'
                                                marginTop='4px'
                                                sx={{
                                                    fontSize: '12px',
                                                    lineHeight: 1.5,
                                                    minHeight: '36px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {video.description?.trim() || 'Watch this tutorial to learn more.'}
                                            </Typography>

                                            {isAdmin && Array.isArray( video.audiences ) && video.audiences.length > 0 && (
                                                <Box display='flex' flexWrap='wrap' gap='5px' marginTop='10px'>
                                                    {video.audiences.map( ( role ) => (
                                                        <Box key={role} border={border[1]} borderRadius='20px' padding='2px 8px'>
                                                            <Typography
                                                                variant='caption'
                                                                color='textSecondary'
                                                                sx={{
                                                                    fontSize: '10px',
                                                                }}
                                                            >
                                                                {ROLE_LABELS[role] || role}
                                                            </Typography>
                                                        </Box>
                                                    ) )}
                                                </Box>
                                            )}

                                            <Box flexGrow={1} />

                                            <Box display='flex' alignItems='center' justifyContent='space-between' marginTop='14px'>
                                                <Typography
                                                    variant='caption'
                                                    color='textSecondary'
                                                    sx={{
                                                        fontSize: '11px',
                                                        fontFamily: 'monospace',
                                                        letterSpacing: '0.3px',
                                                    }}
                                                >
                                                    {durationLabel}
                                                </Typography>

                                                <Box display='flex' alignItems='center' gap='3px' sx={{ color: 'primary.main', }} >
                                                    <Typography variant='caption' fontWeight='600' sx={{ fontSize: '12px', }} >
                                                        Watch
                                                    </Typography>

                                                    <ArrowForwardRounded sx={{ fontSize: '17px', }} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                )
                            } )}
                        </Box>
                    </Box>
                ) )}

            {/* MANUAL MODULE SECTIONS */}

            {activeTab === 'manuals' &&
                !loading &&
                !error &&
                manualSections.map( ( section ) => (
                    <Box key={section.section}>
                        <Box display='flex' justifyContent='space-between' alignItems='center' gap='10px' marginBottom='10px'>
                            <Typography
                                variant='subtitle2'
                                fontWeight='600'
                                color='textSecondary'
                                sx={{
                                    textTransform: 'uppercase',
                                }}
                            >
                                Module: {section.section}
                            </Typography>

                            <Typography variant='caption' color='textSecondary'>
                                {section.files.length} {section.files.length === 1 ? 'file' : 'files'}
                            </Typography>
                        </Box>

                        <Box
                            display='grid'
                            gridTemplateColumns={{
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(3, minmax(0, 1fr))',
                            }}
                            gap={layout.cardsGap || '10px'}
                        >
                            {section.files.map( ( manual ) => {
                                const fileDetails = getFileDetails( manual )
                                const sizeLabel = getFileSizeLabel( fileDetails?.size )
                                return (
                                    <Box
                                        key={manual.id}
                                        bgcolor={palette.contentBg}
                                        border={border[1]}
                                        borderRadius='12px'
                                        overflow='hidden'
                                        onClick={() => openManual( manual )}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'all 180ms ease',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            '&:hover': {
                                                boxShadow: 1,
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                aspectRatio: '16 / 7',
                                                bgcolor: palette.greyedOut,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderBottom: border[1],
                                            }}
                                        >
                                            <PictureAsPdfRounded
                                                color='error'
                                                sx={{
                                                    fontSize: 64,
                                                }}
                                            />
                                        </Box>

                                        <Box padding='14px' display='flex' flexDirection='column' flexGrow={1}>
                                            <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap='8px'>
                                                <Typography
                                                    variant='subtitle2'
                                                    component='h3'
                                                    fontWeight='600'
                                                    color='textPrimary'
                                                    sx={{
                                                        fontSize: '14px',
                                                        lineHeight: 1.4,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {manual.title}
                                                </Typography>

                                                {isAdmin && (
                                                    <Box display='flex' gap='2px' flexShrink={0} marginTop='-5px' marginRight='-5px'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={( event ) => {
                                                                event.stopPropagation()
                                                                openEditManual( manual )
                                                            }}
                                                        >
                                                            {Icons.default.EditIcon}
                                                        </IconButton>

                                                        <IconButton
                                                            size='small'
                                                            color='error'
                                                            onClick={( event ) => {
                                                                event.stopPropagation()
                                                                deleteManual( manual )
                                                            }}
                                                        >
                                                            {Icons.default.DeleteIcon}
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Typography
                                                variant='body2'
                                                color='textSecondary'
                                                marginTop='4px'
                                                sx={{
                                                    fontSize: '12px',
                                                    lineHeight: 1.5,
                                                    minHeight: '36px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {manual.description?.trim() || 'Open this manual to view the complete documentation.'}
                                            </Typography>

                                            {isAdmin && Array.isArray( manual.audiences ) && manual.audiences.length > 0 && (
                                                <Box display='flex' flexWrap='wrap' gap='5px' marginTop='10px'>
                                                    {manual.audiences.map( ( role ) => (
                                                        <Box key={role} border={border[1]} borderRadius='20px' padding='2px 8px'>
                                                            <Typography
                                                                variant='caption'
                                                                color='textSecondary'
                                                                sx={{
                                                                    fontSize: '10px',
                                                                }}
                                                            >
                                                                {ROLE_LABELS[role] || role}
                                                            </Typography>
                                                        </Box>
                                                    ) )}
                                                </Box>
                                            )}

                                            <Box flexGrow={1} />

                                            <Box display='flex' alignItems='center' justifyContent='space-between' marginTop='14px'>
                                                <Typography
                                                    variant='caption'
                                                    color='textSecondary'
                                                    sx={{
                                                        fontSize: '11px',
                                                    }}
                                                >
                                                    {sizeLabel ? `PDF · ${sizeLabel}` : 'PDF'}
                                                </Typography>

                                                <Box
                                                    display='flex'
                                                    alignItems='center'
                                                    gap='3px'
                                                    sx={{
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    <Typography
                                                        variant='caption'
                                                        fontWeight='600'
                                                        sx={{
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        Open
                                                    </Typography>

                                                    <ArrowForwardRounded
                                                        sx={{
                                                            fontSize: '17px',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                )
                            } )}
                        </Box>
                    </Box>
                ) )}

            {/* TUTORIAL VIEW DIALOG */}

            <Dialog
                disableRestoreFocus
                fullScreen={xs}
                maxWidth='md'
                fullWidth
                open={viewerOpen}
                onClose={closeTutorialViewer}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'hidden',
                            display: 'flex',
                            maxHeight: {
                                xs: '100vh',
                                sm: '90vh',
                            },
                            borderRadius: {
                                xs: 0,
                                sm: layout.borderRadius,
                            },
                        },
                    },
                }}
            >
                {selectedTutorial && (
                    <Box
                        display='flex'
                        flexDirection='column'
                        height={{
                            xs: '100vh',
                            sm: '90vh',
                        }}
                    >
                        <Box
                            display='flex'
                            alignItems='flex-start'
                            justifyContent='space-between'
                            gap='14px'
                            padding={{
                                xs: '12px 15px',
                                sm: '14px 20px',
                            }}
                            borderBottom={border[1]}
                            flexShrink={0}
                        >
                            <Box minWidth={0}>
                                <Typography variant='h6' fontWeight='600' lineHeight='1.3' noWrap>
                                    {selectedTutorial.title}
                                </Typography>

                                <Box display='flex' alignItems='center' gap='8px' flexWrap='wrap' marginTop='4px'>
                                    <Typography variant='caption' color='textSecondary'>
                                        Module: {selectedTutorial.module}
                                    </Typography>

                                    {getDurationLabel( getTutorialDuration( selectedTutorial, videoDurations ) ) && (
                                        <>
                                            <Box width='3px' height='3px' borderRadius='50%' bgcolor={palette.text.disabled} />

                                            <Typography variant='caption' color='textSecondary'>
                                                {getDurationLabel( getTutorialDuration( selectedTutorial, videoDurations ) )}
                                            </Typography>
                                        </>
                                    )}

                                    {getFileSizeLabel( getFileDetails( selectedTutorial )?.size ) && (
                                        <>
                                            <Box width='3px' height='3px' borderRadius='50%' bgcolor={palette.text.disabled} />

                                            <Typography variant='caption' color='textSecondary'>
                                                {getFileSizeLabel( getFileDetails( selectedTutorial )?.size )}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <IconButton size='small' onClick={closeTutorialViewer}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>

                        <Box flexGrow={1} minHeight={0} overflow='auto'>
                            <Box bgcolor='black' width='100%'>
                                <VideoPlayer title={selectedTutorial.title} src={selectedTutorial.video_url} containerHeight={getViewerVideoHeight( selectedTutorial )} controls={true} autoPlay={true} />
                            </Box>

                            <Box padding={{ xs: '14px 15px', sm: '16px 20px', }} bgcolor={palette.background.paper} >
                                {isAdmin && Array.isArray( selectedTutorial.audiences ) && selectedTutorial.audiences.length > 0 && (
                                    <Box display='flex' flexWrap='wrap' gap='5px' marginBottom='12px'>
                                        {selectedTutorial.audiences.map( ( role ) => (
                                            <Box key={role} border={border[1]} borderRadius='20px' padding='3px 9px'>
                                                <Typography variant='caption' color='textSecondary'>
                                                    {ROLE_LABELS[role] || role}
                                                </Typography>
                                            </Box>
                                        ) )}
                                    </Box>
                                )}

                                <Typography variant='subtitle2' fontWeight='600' marginBottom='5px'>
                                    Description
                                </Typography>

                                <Typography
                                    variant='body2'
                                    color='textSecondary'
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {selectedTutorial.description?.trim() || 'No description available.'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Dialog>

            {previewFile && <DrawerDocumentViewer fileName={previewingFileDetails?.name || 'Manual'} file={previewFile} fileDetails={previewingFileDetails} type='pdf' loadingText='Loading manual...' setPreviewFile={setPreviewFile} setPreviewingFileDetails={setPreviewingFileDetails} />}

            <ConfirmDialog
                status={Boolean( deleteTarget )}
                waiting={deleting}
                actionName='Delete'
                message={`Delete "${deleteTarget?.item?.title || ''}"?`}
                confirmAction={confirmDelete}
                cancelAction={cancelDelete}
            />

            {/* ADMIN DIALOGS */}

            {isAdmin && (
                <>
                    <AddTutorialDialog open={addDialogOpen} setOpen={setAddDialogOpen} tutorial={editingTutorial} refresh={fetchTutorials} />

                    <AddManualDialog open={manualDialogOpen} setOpen={setManualDialogOpen} manual={editingManual} refresh={fetchManuals} />
                </>
            )}
        </Box>
    )
}
export default TutorialsSection
